import React, { useCallback, useEffect, useState } from "react";
import type { DatePickerProps, TableColumnsType, TableProps } from "antd";
import { Form, FormInstance, Modal, Progress, Table, notification, Spin, Tooltip } from "antd"
import useSWR from "swr";
import axios from "axios";
import { DataType as PoModalDataType } from "../pages/inventory/data_po_for_goods_receive";
import { AddLineForGRDataType } from "../pages/inventory/add_line_for_goods_receive";
import { debounce, isEqual } from "lodash";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export interface WarehouseDataType {
     warehouse_code: string;
     warehouse_name: string;
}

export const itemGroupOptions = [
     { value: "", label: "-- Empty --" },
     { value: "1", label: "Inventory" },
     { value: "2", label: "Outside Job" },
];

export const itemGroupLabelMap: Record<string, string> = itemGroupOptions.reduce(
     (acc, option) => ({ ...acc, [option.value]: option.label }),
     {} as Record<string, string>,
);

export const getItemGroupLabel = (value?: string | null): string => {
     if (value === null || value === undefined) return "";
     return itemGroupLabelMap[value] ?? value;
};

export interface PoItemDataType {
     key: string;
     item_code: string;
     item_name: string;
     case: number;
     uom: string;
     uom_rate: string;
     location: string;
     ref: string;
     do: string;
     receive: string;
     short: string;
     dmg: string;
     over: string;
     wrong: string;
     price: string;
     do_no: string;
     discount_per_item?: string;
     ref_document_no?: string;
}

interface PoItemBackendType {
     item_code: string;
     item_name: string;
     uom: string;
     uom_rate: string;
     price: number;
     qty: number;
     discount: number;
}

export interface GoodsReceiveProps {
     onDateChange: DatePickerProps["onChange"];
     onChange: (value: string) => void;
     onItemGroupChange: (value: string) => void;
     columns: TableColumnsType<PoItemDataType>;
     rowSelection: TableProps<PoItemDataType>["rowSelection"];
     isModalOpen: boolean;
     showModal: () => void;
     handleCancel: () => void;
     poData: PoModalDataType[];
     onSelectPo: (po: PoModalDataType, incompleteItems?: PoItemDataType[]) => void;
     form: FormInstance;
     tableDataSource: PoItemDataType[];
     isEditModalOpen: boolean;
     showEditModal: () => void;
     handleEditCancel: () => void;
     selectedPoItemForEdit: PoItemDataType | null;
     onRowDoubleClick: (record: PoItemDataType) => void;
     onUpdatePoItem: (updatedItem: PoItemDataType) => void;
     onSelectLocationCode: (locationCode: string) => void;
     isAddLineModalOpen: boolean;
     showAddLineModal: () => void;
     handleAddLineCancel: () => void;
     refDocumentNo?: string;
     onAddLineItems: (items: AddLineForGRDataType[]) => void;
     onDeleteSelectedItems: () => void;
     initialSelectedAddLineKeys: React.Key[];
     autosaveStatus: "idle" | "processing" | "error" | "stopped";
     existingGrPoNos: string[];
     tableSummary: (data: readonly PoItemDataType[]) => React.ReactNode;
     onVoid: () => void;
     onSubmit: () => void;
     onPrint: () => void;
     printLoading: boolean;
     printProgress: number;
     warehouseData: WarehouseDataType[];
     submitLoading: boolean;
     lockedFields: Record<string, { userName: string; socketId: string }>;
     onFieldFocus: (fieldName: string) => void;
     onFieldBlur: (fieldName: string) => void;
     onFormValuesChange: (changedValues: any, allValues: any) => void;
     getLockProps: (fieldName: string) => { help: string | undefined; validateStatus: "warning" | undefined };
}

const fetcher = (url: string) => axios.get(url).then((res) => res.data);
const numberToWordsID = (num: number): string => {
     const units = ['', 'satu', 'dua', 'tiga', 'empat', 'lima', 'enam', 'tujuh', 'delapan', 'sembilan'];
     const teens = ['sepuluh', 'sebelas', 'dua belas', 'tiga belas', 'empat belas', 'lima belas', 'enam belas', 'tujuh belas', 'delapan belas', 'sembilan belas'];
     const tens = ['', '', 'dua puluh', 'tiga puluh', 'empat puluh', 'lima puluh', 'enam puluh', 'tujuh puluh', 'delapan puluh', 'sembilan puluh'];
     const scales = ['', 'ribu', 'juta', 'milyar', 'triliun'];

     if (num === 0) return 'nol';

     let words = '';
     let i = 0;

     const convertChunk = (n: number): string => {
          let chunkWords = '';
          if (n >= 100) {
               chunkWords += units[Math.floor(n / 100)] + ' ratus ';
               n %= 100;
          }
          if (n >= 10 && n <= 19) {
               chunkWords += teens[n - 10];
               n = 0;
          } else if (n >= 20) {
               chunkWords += tens[Math.floor(n / 10)] + ' ';
               n %= 10;
          }
          if (n > 0) {
               chunkWords += units[n];
          }
          return chunkWords.trim();
     };

     while (num > 0) {
          const chunk = num % 1000;
          if (chunk > 0) {
               let chunkWords = convertChunk(chunk);
               if (i === 1 && chunkWords === 'satu') {
                    chunkWords = 'seribu';
               }
               words = chunkWords + ' ' + scales[i] + ' ' + words;
          }
          num = Math.floor(num / 1000);
          i++;
     }

     return words.replace(/\s+/g, ' ').trim();
};
const withGoodsReceiveLogic = <P extends GoodsReceiveProps>(WrappedComponent: React.ComponentType<P>) => {
     const HOC: React.FC<Omit<P, keyof GoodsReceiveProps>> = (props) => {
          const [form] = Form.useForm();
          const navigate = useNavigate();
          const location = useLocation();
          const goodsReceiveNoFromState = location.state?.goodsReceiveNo;
          const [tableDataSource, setTableDataSource] = useState<PoItemDataType[]>([]);
          const [selectedPoItemForEdit, setSelectedPoItemForEdit] = useState<PoItemDataType | null>(null);
          const [isAddLineModalOpen, setIsAddLineModalOpen] = useState(false);
          const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
          const [initialSelectedAddLineKeys, setInitialSelectedAddLineKeys] = useState<React.Key[]>([]);
          const [autosaveStatus, setAutosaveStatus] = useState<"idle" | "processing" | "error" | "stopped">(
               "stopped",
          );
          const [lastSavedFormValues, setLastSavedFormValues] = useState<any>(null);
          const [lastSavedTableData, setLastSavedTableData] = useState<PoItemDataType[]>([]);
          const [isEditMode, setIsEditMode] = useState(false);
          const formValues = Form.useWatch([], form);
          const [printLoading, setPrintLoading] = useState<boolean>(false);
          const [printProgress, setPrintProgress] = useState<number>(0);
          const [submitLoading, setSubmitLoading] = useState<boolean>(false);
          const { socket, userData } = useAuth();
          const [lockedFields, setLockedFields] = useState<Record<string, { userName: string; socketId: string }>>({});
          const currentGrNo = Form.useWatch("goods_receive_no", form) || goodsReceiveNoFromState;

          const { data: warehouseData = [] } = useSWR<WarehouseDataType[]>(
               `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-warehouses`,
               fetcher,
          );

          useEffect(() => {
               form.setFieldsValue({ status: "Draft" });
          }, [form]);

          useEffect(() => {
               if (!socket || !currentGrNo) return;
               socket.emit("join_gr_room", { grNo: currentGrNo });

               const handleCurrentLocks = (locks: Record<string, { userName: string; socketId: string }>) => {
                    setLockedFields(locks);
               };
               const handleFieldLocked = (data: { fieldName: string; userName: string; socketId: string }) => {
                    setLockedFields((prev) => ({ ...prev, [data.fieldName]: { userName: data.userName, socketId: data.socketId } }));
               };
               const handleFieldUnlocked = (data: { fieldName: string }) => {
                    setLockedFields((prev) => {
                         const updated = { ...prev };
                         delete updated[data.fieldName];
                         return updated;
                    });
               };
               const handleReceiveSyncData = (data: { changedValues?: any; allValues?: any; tableItems?: PoItemDataType[] }) => {
                    const parseSyncDate = (values: any) => {
                         if (!values) return values;
                         const formatted = { ...values };
                         if (formatted.date) {
                              formatted.date = moment(formatted.date).isValid() ? moment(formatted.date) : null;
                         }
                         return formatted;
                    };
                    if (data.changedValues) {
                         form.setFieldsValue(parseSyncDate(data.changedValues));
                    }
                    if (data.allValues) {
                         form.setFieldsValue(parseSyncDate(data.allValues));
                    }
                    if (data.tableItems) {
                         setTableDataSource(data.tableItems);
                    }
               };

               socket.on("gr_current_locks", handleCurrentLocks);
               socket.on("gr_field_locked", handleFieldLocked);
               socket.on("gr_field_unlocked", handleFieldUnlocked);
               socket.on("gr_receive_sync_data", handleReceiveSyncData);

               return () => {
                    socket.emit("leave_gr_room", { grNo: currentGrNo });
                    socket.off("gr_current_locks", handleCurrentLocks);
                    socket.off("gr_field_locked", handleFieldLocked);
                    socket.off("gr_field_unlocked", handleFieldUnlocked);
                    socket.off("gr_receive_sync_data", handleReceiveSyncData);
               };
          }, [socket, currentGrNo, form]);

          const onFieldFocus = useCallback((fieldName: string) => {
               if (socket && currentGrNo) {
                    socket.emit("gr_lock_field", {
                         grNo: currentGrNo,
                         fieldName,
                         userName: userData?.nama || "User Lain"
                    });
               }
          }, [socket, currentGrNo, userData]);

          const onFieldBlur = useCallback((fieldName: string) => {
               if (socket && currentGrNo) {
                    socket.emit("gr_unlock_field", {
                         grNo: currentGrNo,
                         fieldName
                    });
               }
          }, [socket, currentGrNo]);

          const onFormValuesChange = useCallback((changedValues: any, allValues: any) => {
               if (socket && currentGrNo) {
                    socket.emit("gr_sync_field_data", {
                         grNo: currentGrNo,
                         changedValues,
                         allValues,
                         tableItems: tableDataSource
                    });
               }
          }, [socket, currentGrNo, tableDataSource]);

          const getLockProps = useCallback((fieldName: string) => ({
               help: lockedFields?.[fieldName] ? `Sedang diedit oleh ${lockedFields[fieldName].userName}` : undefined,
               validateStatus: (lockedFields?.[fieldName] ? "warning" : undefined) as "warning" | undefined,
          }), [lockedFields]);

          useEffect(() => {
               if (goodsReceiveNoFromState) {
                    const loadGoodsReceiveData = async () => {
                         try {
                              const response = await axios.get(
                                   `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive/${goodsReceiveNoFromState}`,
                              );
                              const { header, items } = response.data;
                              let displayStatusString = String(header.status);
                              if (header.status === 1 || header.status === "1") {
                                   displayStatusString = "Draft";
                              } else if (header.status === 4 || header.status === "4") {
                                   displayStatusString = "Complete";
                              }

                              form.setFieldsValue({
                                   status: displayStatusString,
                                   goods_receive_no: header.goods_receive_no,
                                   date: header.date ? moment(header.date) : null,
                                   item_group: header.item_group,
                                   reference_type: header.reference_type,
                                   ref_document_no: header.ref_document_no,
                                   supplier_code: header.supplier_code,
                                   supplier_name: header.supplier_name,
                                   supplier_invoice_no: header.supplier_invoice_no,
                                   tax_invoice_no: header.tax_invoice_no,
                                   warehouse: header.warehouse,
                                   via_expedition: header.via_expedition,
                                   no_delivery: header.no_delivery,
                                   remark: header.remark,
                              });

                              const transformedItems: PoItemDataType[] = items.map((item: any) => ({
                                   key: item.item_code,
                                   item_code: item.item_code,
                                   item_name: item.item_name,
                                   case: item.case,
                                   uom: item.uom,
                                   uom_rate: item.uom_rate,
                                   location: item.location,
                                   ref: item.ref.toString(),
                                   do: item.do.toString(),
                                   receive: item.receive.toString(),
                                   short: item.short.toString(),
                                   dmg: item.dmg.toString(),
                                   over: item.over.toString(),
                                   wrong: item.wrong.toString(),
                                   price: item.price.toString(),
                                   do_no: item.do_no,
                                   discount_per_item: item.discount_per_item ? item.discount_per_item.toString() : "0",
                              }));
                              setTableDataSource(transformedItems);
                              setIsEditMode(true);

                              if (header.status === "1") {
                                   setAutosaveStatus("idle");
                              } else if (header.status === "4") {
                                   setAutosaveStatus("stopped");
                              } else {
                                   setAutosaveStatus("stopped");
                              }

                              setLastSavedFormValues(form.getFieldsValue(true));
                              setLastSavedTableData(transformedItems);
                         } catch (error) {
                              console.error("Error loading Goods Receive data:", error);
                              setAutosaveStatus("error");
                         }
                    };
                    loadGoodsReceiveData();
               } else {
                    setIsEditMode(false);
                    setAutosaveStatus("idle");
               }
          }, [goodsReceiveNoFromState, form]);

          const onDateChange: DatePickerProps["onChange"] = (date, dateString) => {
               console.log("Selected Date: ", dateString);
          };

          const onChange = (value: string) => {
               console.log(`Selected: ${value}`);
          };

          const onItemGroupChange = async (value: string) => {
               console.log(`Selected Item Group: ${value}`);

               if (value !== "" && !isEditMode) {
                    try {
                         const response = await axios.get(
                              `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-no`,
                         );
                         const generatedNo = response.data.goodsReceiveNo;

                         form.setFieldsValue({ goods_receive_no: generatedNo });
                    } catch (error) {
                         console.error("Error generating Goods Receive No:", error);
                         form.setFieldsValue({ goods_receive_no: "Error generating number" });

                         setAutosaveStatus("error");
                    }
               } else if (value === "") {
                    form.setFieldsValue({ goods_receive_no: "" });
                    setAutosaveStatus("stopped");
               }
          };

          const handlePoSelection = async (po: PoModalDataType, incompleteItems?: PoItemDataType[]) => {
               form.setFieldsValue({
                    ref_document_no: po.po_no,
                    supplier_code: po.supplier_code,
                    supplier_name: po.supplier_name,
                    warehouse: po.warehouse_code,
               });
               handleCancel();

               const fetchLocationForItems = async (items: PoItemDataType[]): Promise<PoItemDataType[]> => {
                    return Promise.all(items.map(async (item) => {
                         try {
                              const locationResponse = await axios.get(
                                   `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-location-by-item/${item.item_code}?warehouse=${po.warehouse_code}`
                              );
                              return { ...item, location: locationResponse.data.location_code_rack || "" };
                         } catch (error) {
                              console.error(`Error fetching location for item ${item.item_code}:`, error);
                              return { ...item, location: "" };
                         }
                    }));
               };

               if (incompleteItems && incompleteItems.length > 0) {
                    setTableDataSource(incompleteItems);
                    if (socket && currentGrNo) {
                         socket.emit("gr_sync_field_data", {
                              grNo: currentGrNo,
                              allValues: form.getFieldsValue(true),
                              tableItems: incompleteItems
                         });
                    }
                    return;
               }

               try {
                    const response = await axios.get(
                         `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-po-items/${po.po_no}?warehouse=${po.warehouse_code}`,
                    );
                    const poItems: PoItemDataType[] = response.data.map((item: any) => {
                         const discVal = parseFloat(item.discount || "0");
                         const qtyVal = parseFloat(item.qty || "0");
                         const discPerItem = qtyVal > 0 ? (discVal / qtyVal).toString() : "0";
                         return {
                              key: item.item_code,
                              item_code: item.item_code,
                              item_name: item.item_name,
                              case: 0,
                              uom: item.uom,
                              uom_rate: item.uom_rate,
                              ref: (parseFloat(item.qty) - parseFloat(item.total_received)).toString(),
                              do: "",
                              receive: "0",
                              short: "0",
                              dmg: "0",
                              over: "0",
                              wrong: "0",
                              price: item.price.toString(),
                              do_no: "",
                              discount_per_item: discPerItem,
                              location: item.location || "",
                         };
                    });
                    setTableDataSource(poItems);
                    if (socket && currentGrNo) {
                         socket.emit("gr_sync_field_data", {
                              grNo: currentGrNo,
                              allValues: form.getFieldsValue(true),
                              tableItems: poItems
                         });
                    }
               } catch (error) {
                    console.error("Error fetching PO items:", error);
                    setTableDataSource([]);
               }
          };

          const columns: TableColumnsType<PoItemDataType> = [
               {
                    title: "Item Code",
                    dataIndex: "item_code",
                    render: (text: string, record: PoItemDataType) => {
                         const lock = lockedFields[`row_${record.key}`];
                         return lock ? (
                              <Tooltip title={`Sedang diedit oleh ${lock.userName}`}>
                                   <span style={{ color: "#faad14", fontWeight: "bold" }}>
                                        {text} (🔒 {lock.userName})
                                   </span>
                              </Tooltip>
                         ) : (
                              text
                         );
                    },
               },
               { title: "Item Name", dataIndex: "item_name" },
               { title: "Case", dataIndex: "case" },
               { title: "UOM", dataIndex: "uom" },
               { title: "UoM Rate", dataIndex: "uom_rate" },
               { title: "Location", dataIndex: "location" },
               { title: "Ref", dataIndex: "ref" },
               { title: "DO", dataIndex: "do" },
               { title: "Receive", dataIndex: "receive" },
               { title: "Short", dataIndex: "short" },
               { title: "DMG", dataIndex: "dmg" },
               { title: "Over", dataIndex: "over" },
               { title: "Wrong", dataIndex: "wrong" },
               {
                    title: "Item Price",
                    dataIndex: "price",
                    render: (text: string) => parseFloat(text || "0").toLocaleString("id-ID"),
               },
               { title: "DO No", dataIndex: "do_no" },
          ];

          const rowSelection: TableProps<PoItemDataType>["rowSelection"] = {
               selectedRowKeys,
               onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
               getCheckboxProps: (record: PoItemDataType) => ({
                    disabled: !!lockedFields[`row_${record.key}`],
               }),
          };
          const [isModalOpen, setIsModalOpen] = useState(false);
          const showModal = () => {
               setIsModalOpen(true);
          };
          const handleCancel = () => {
               setIsModalOpen(false);
          };

          const [isEditModalOpen, setIsEditModalOpen] = useState(false);
          const showEditModal = () => {
               setIsEditModalOpen(true);
          };
          const handleEditCancel = () => {
               if (selectedPoItemForEdit) {
                    onFieldBlur(`row_${selectedPoItemForEdit.key}`);
               }
               setIsEditModalOpen(false);
               setSelectedPoItemForEdit(null);
          };

          const handleRowDoubleClick = (record: PoItemDataType) => {
               const lockKey = `row_${record.key}`;
               if (lockedFields[lockKey]) {
                    notification.warning({
                         message: "Baris Sedang Terkunci",
                         description: `Item ini sedang diedit oleh ${lockedFields[lockKey].userName}`
                    });
                    return;
               }
               onFieldFocus(lockKey);
               setSelectedPoItemForEdit(record);
               showEditModal();
          };

          const handleUpdatePoItem = (updatedItem: PoItemDataType) => {
               const newDataSource = tableDataSource.map((item) => (item.key === updatedItem.key ? updatedItem : item));
               setTableDataSource(newDataSource);
               if (socket && currentGrNo) {
                    socket.emit("gr_sync_field_data", {
                         grNo: currentGrNo,
                         allValues: form.getFieldsValue(true),
                         tableItems: newDataSource
                    });
               }
               handleEditCancel();
          };

          const handleSelectLocationCodeForEdit = (locationCode: string) => {
               if (selectedPoItemForEdit) {
                    const updatedItem = { ...selectedPoItemForEdit, location: locationCode };
                    handleUpdatePoItem(updatedItem);
                    setSelectedPoItemForEdit(updatedItem);
               }
          };

          const showAddLineModal = async () => {
               const currentRefDocumentNo = form.getFieldValue("ref_document_no");
               if (currentRefDocumentNo) {
                    try {
                         const response = await axios.get(
                              `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-po-items/${currentRefDocumentNo}`,
                         );
                         const fetchedItems: PoItemBackendType[] = response.data;
                         const existingItemCodes = new Set(tableDataSource.map((item) => item.item_code));
                         const selectableItems = fetchedItems.filter(
                              (item) => !existingItemCodes.has(item.item_code),
                         );
                         setInitialSelectedAddLineKeys([]);
                    } catch (error) {
                         console.error("Error fetching PO items for Add Line pre-selection:", error);
                         setInitialSelectedAddLineKeys([]);
                    }
               } else {
                    setInitialSelectedAddLineKeys([]);
               }
               setIsAddLineModalOpen(true);
          };

          const handleAddLineCancel = () => {
               setIsAddLineModalOpen(false);
               setInitialSelectedAddLineKeys([]);
          };

          const handleAddLineItems = (newItems: AddLineForGRDataType[]) => {
               const transformedItems: PoItemDataType[] = newItems.map((item) => ({
                    key: item.itemCode,
                    item_code: item.itemCode,
                    item_name: item.itemName,
                    case: 0,
                    uom: "",
                    uom_rate: "",
                    location: "",
                    ref: item.qty.toString(),
                    do: "",
                    receive: "0",
                    short: "0",
                    dmg: "0",
                    over: "0",
                    wrong: "0",
                    price: item.itemPrice.toString(),
                    do_no: "",
                    discount_per_item: "0",
               }));
               const newDataSource = [...tableDataSource, ...transformedItems];
               setTableDataSource(newDataSource);
               if (socket && currentGrNo) {
                    socket.emit("gr_sync_field_data", {
                         grNo: currentGrNo,
                         allValues: form.getFieldsValue(true),
                         tableItems: newDataSource
                    });
               }
               handleAddLineCancel();
          };

          const handleDeleteSelectedItems = () => {
               const newDataSource = tableDataSource.filter((item) => !selectedRowKeys.includes(item.key));
               setTableDataSource(newDataSource);
               if (socket && currentGrNo) {
                    socket.emit("gr_sync_field_data", {
                         grNo: currentGrNo,
                         allValues: form.getFieldsValue(true),
                         tableItems: newDataSource
                    });
               }
               setSelectedRowKeys([]);
          };

          const { data: poData = [], error: poDataError } = useSWR<PoModalDataType[]>(
               isModalOpen ? `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-po-data` : null,
               fetcher,
          );

          if (poDataError) {
               console.error("Error fetching PO data for Goods Receive:", poDataError);
          }

          const { data: existingGrPoNos = [], error: existingGrPoNosError } = useSWR<string[]>(
               isModalOpen ? `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-existing-po-nos` : null,
               fetcher,
          );

          if (existingGrPoNosError) {
               console.error("Error fetching existing GR PO numbers:", existingGrPoNosError);
          }

          const calculateTotalItemPrice = (dataSource: PoItemDataType[]): number => {
               return dataSource.reduce((sum, item) => sum + (parseFloat(item.price || "0") * parseFloat(item.receive || "0")), 0);
          };

          const calculateTotalReceiveQty = (dataSource: readonly PoItemDataType[]): number => {
               return dataSource.reduce((sum, item) => sum + parseFloat(item.receive || "0"), 0);
          };

          const autosave = useCallback(
               async (currentFormValues: any, currentTableData: PoItemDataType[]) => {
                    const grNo = currentFormValues.goods_receive_no;

                    if (
                         !grNo ||
                         (grNo === lastSavedFormValues?.goods_receive_no &&
                              isEqual(currentFormValues, lastSavedFormValues) &&
                              isEqual(currentTableData, lastSavedTableData))
                    ) {
                         setAutosaveStatus("idle");
                         return;
                    }

                    setAutosaveStatus("processing");
                    try {
                         const totalItemPrice = calculateTotalItemPrice(currentTableData);
                         const totalReceiveQty = calculateTotalReceiveQty(currentTableData);
                         const payload = {
                              goodsReceiveData: {
                                   status: currentFormValues.status === "Draft" ? "1" : currentFormValues.status,
                                   goods_receive_no: currentFormValues.goods_receive_no,
                                   date: currentFormValues.date
                                        ? moment(currentFormValues.date).format("YYYY-MM-DD")
                                        : null,
                                   item_group: currentFormValues.item_group,
                                   reference_type: currentFormValues.reference_type,
                                   ref_document_no: currentFormValues.ref_document_no,
                                   supplier_code: currentFormValues.supplier_code,
                                   supplier_name: currentFormValues.supplier_name,
                                   supplier_invoice_no: currentFormValues.supplier_invoice_no,
                                   tax_invoice_no: currentFormValues.tax_invoice_no,
                                   warehouse: currentFormValues.warehouse,
                                   total_amount: totalItemPrice,
                                   total_receive: totalReceiveQty,
                                   via_expedition: currentFormValues.via_expedition,
                                   no_delivery: currentFormValues.no_delivery,
                                   remark: currentFormValues.remark,
                              },
                              tableItems: currentTableData.map((item) => ({
                                   goods_receive_no: grNo,
                                   item_code: item.item_code,
                                   item_name: item.item_name,
                                   case: item.case,
                                   uom: item.uom,
                                   uom_rate: item.uom_rate,
                                   location: item.location,
                                   ref: item.ref,
                                   do: item.do,
                                   receive: item.receive,
                                   short: item.short,
                                   dmg: item.dmg,
                                   over: item.over,
                                   wrong: item.wrong,
                                   item_price: item.price,
                                   do_no: item.do_no,
                                   discount_per_item: item.discount_per_item || "0",
                                   ref_document_no: currentFormValues.ref_document_no,
                              })),
                         };
                         const apiCall = axios.post(
                              `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-autosave`,
                              payload,
                         );
                         const delay = new Promise((resolve) => setTimeout(resolve, 500));
                         await Promise.all([apiCall, delay]);
                         setAutosaveStatus("idle");
                         setLastSavedFormValues(currentFormValues);
                         setLastSavedTableData(currentTableData);
                    } catch (error) {
                         console.error("Autosave failed:", error);
                         setAutosaveStatus("error");
                    }
               },
               [lastSavedFormValues, lastSavedTableData, calculateTotalItemPrice, calculateTotalReceiveQty],
          );

          const handleSubmit = async () => {
               try {
                    await form.validateFields();
                    const currentFormValues = form.getFieldsValue(true);
                    const goodsReceiveNo = currentFormValues.goods_receive_no;
                    const refDocumentNo = currentFormValues.ref_document_no;
                    const currentStatus = currentFormValues.status;

                    if (!goodsReceiveNo) {
                         notification.warning({ message: "Peringatan", description: "Goods Receive No. tidak ditemukan." });
                         return;
                    }

                    const isDraft = currentStatus === 1 || currentStatus === "1" || currentStatus === "Draft";
                    if (!isDraft) {
                         notification.warning({ message: "Peringatan", description: "Hanya Goods Receive dengan status 'Draft' yang dapat disubmit." });
                         return;
                    }

                    const itemsWithEmptyReceive = tableDataSource.filter(item =>
                         !item.receive || parseFloat(item.receive) === 0
                    );

                    if (itemsWithEmptyReceive.length > 0) {
                         const warningMessage = (
                              <div>
                                   <p>Kolom "Receive" tidak boleh kosong atau nol untuk item berikut:</p>
                                   <ul>
                                        {itemsWithEmptyReceive.map((item, index) => (
                                             <li key={index}>
                                                  <strong>Item Code:</strong> {item.item_code}, <strong>Item Name:</strong> {item.item_name}
                                             </li>
                                        ))}
                                   </ul>
                              </div>
                         );
                         Modal.warning({
                              title: "Peringatan",
                              content: warningMessage,
                              okText: "OK",
                         });
                         return;
                    }

                    const itemsWithEmptyLocation = tableDataSource.filter(item =>
                         !item.location || item.location.trim() === ""
                    );

                    if (itemsWithEmptyLocation.length > 0) {
                         const warningLocationMessage = (
                              <div>
                                   <p>Kolom "Location" tidak boleh kosong untuk item berikut:</p>
                                   <ul>
                                        {itemsWithEmptyLocation.map((item, index) => (
                                             <li key={index}>
                                                  <strong>Item Code:</strong> {item.item_code}, <strong>Item Name:</strong> {item.item_name}
                                             </li>
                                        ))}
                                   </ul>
                              </div>
                         );
                         Modal.warning({
                              title: "Peringatan",
                              content: warningLocationMessage,
                              okText: "OK",
                         });
                         return;
                    }

                    Modal.confirm({
                         title: "Konfirmasi Submit",
                         content: `Apakah Data Sudah Sesuai Pada. ${goodsReceiveNo}? `,
                         okText: "Ya",
                         cancelText: "Tidak",
                         onOk: async () => {
                              setSubmitLoading(true);
                              setAutosaveStatus("processing");
                              const totalItemPrice = calculateTotalItemPrice(tableDataSource);
                              const totalReceiveQty = calculateTotalReceiveQty(tableDataSource);

                              const payload = {
                                   goodsReceiveData: {
                                        ...currentFormValues,
                                        status: "4",
                                        date: currentFormValues.date
                                             ? moment(currentFormValues.date).format("YYYY-MM-DD")
                                             : null,
                                        total_amount: totalItemPrice,
                                        total_receive: totalReceiveQty,
                                   },
                                   tableItems: tableDataSource.map((item) => ({
                                        goods_receive_no: goodsReceiveNo,
                                        item_code: item.item_code,
                                        item_name: item.item_name,
                                        case: item.case,
                                        uom: item.uom,
                                        uom_rate: item.uom_rate,
                                        location: item.location,
                                        ref: item.ref,
                                        do: item.do,
                                        receive: item.receive,
                                        short: item.short,
                                        dmg: item.dmg,
                                        over: item.over,
                                        wrong: item.wrong,
                                        item_price: item.price,
                                        do_no: item.do_no,
                                        discount_per_item: item.discount_per_item || "0",
                                        ref_document_no: refDocumentNo,
                                   })),
                              };

                              try {

                                   await axios.post(
                                        `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-submit`,
                                        payload,
                                   );

                                   form.setFieldsValue({ status: "Complete" });
                                   notification.success({ message: "Sukses", description: `Goods Receive No. ${goodsReceiveNo} berhasil disubmit.` });
                                   setAutosaveStatus("stopped");
                                   setIsEditMode(true);
                                   navigate("/dashboard/data_goods_receive");
                              } catch (error: any) {
                                   console.error("Error submitting Goods Receive:", error);
                                   const errorMsg = error.response?.data?.error || "Gagal submit Goods Receive. Silakan coba lagi.";
                                   if (error.response?.status === 400) {
                                        notification.error({
                                             message: "Validasi Gagal",
                                             description: errorMsg,
                                             duration: 0,
                                        });
                                   } else {
                                        notification.error({ message: "Error", description: errorMsg });
                                   }
                                   setAutosaveStatus("error");
                              } finally {
                                   setSubmitLoading(false);
                              }
                         },
                    });
               } catch (info) {
                    console.log("Validate Failed:", info);
                    notification.error({ message: "Error", description: "Harap lengkapi semua field yang diperlukan." });
               }
          };

          useEffect(() => {
               const timeoutId = setTimeout(() => {
                    const grNo = form.getFieldValue("goods_receive_no");
                    const currentStatus = form.getFieldValue("status");
                    const isDraft = currentStatus === 1 || currentStatus === "1" || currentStatus === "Draft";
                    const isComplete = currentStatus === 4 || currentStatus === "4" || currentStatus === "Complete";

                    if (formValues && grNo && isDraft) {
                         autosave(formValues, tableDataSource);
                    } else if (!grNo || !isDraft || isComplete) {
                         setAutosaveStatus("stopped");
                    }
               }, 1500);
               return () => clearTimeout(timeoutId);
          }, [form, formValues, tableDataSource, autosave, isEditMode, goodsReceiveNoFromState]);

          const tableSummary = (data: readonly PoItemDataType[]) => {
               const totalItemPrice = calculateTotalItemPrice(data as PoItemDataType[]);
               const totalReceiveQty = calculateTotalReceiveQty(data);
               return (
                    <Table.Summary fixed>
                         <Table.Summary.Row>
                              <Table.Summary.Cell index={0} colSpan={9}>
                                   <span style={{ textAlign: "right", fontWeight: "bold" }}>Total</span>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={9}>
                                   <span style={{ fontWeight: "bold" }}>
                                        {totalReceiveQty.toLocaleString("id-ID")}
                                   </span>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={11}></Table.Summary.Cell>
                              <Table.Summary.Cell index={12}></Table.Summary.Cell>
                              <Table.Summary.Cell index={13}></Table.Summary.Cell>
                              <Table.Summary.Cell index={14}></Table.Summary.Cell>
                              <Table.Summary.Cell index={15}>
                                   <span style={{ fontWeight: "bold" }}>
                                        {totalItemPrice.toLocaleString("id-ID")}
                                   </span>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={16}></Table.Summary.Cell>
                         </Table.Summary.Row>
                    </Table.Summary>
               );
          };

          const handleVoid = () => {
               const goodsReceiveNo = form.getFieldValue("goods_receive_no");
               const currentStatus = form.getFieldValue("status");

               if (!goodsReceiveNo) {
                    notification.warning({ message: "Peringatan", description: "Goods Receive No. tidak ditemukan." });
                    return;
               }

               const isDraft = currentStatus === 1 || currentStatus === "1" || currentStatus === "Draft";
               if (!isDraft) {
                    notification.warning({ message: "Peringatan", description: "Hanya Goods Receive dengan status 'Draft' yang dapat dibatalkan." });
                    return;
               }

               Modal.confirm({
                    title: "Konfirmasi Pembatalan",
                    content: `Apakah Anda yakin ingin membatalkan Goods Receive No. ${goodsReceiveNo}? Tindakan ini tidak dapat dibatalkan.`,
                    okText: "Ya",
                    cancelText: "Tidak",
                    onOk: async () => {
                         try {
                              setAutosaveStatus("processing");
                              await axios.delete(
                                   `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive/${goodsReceiveNo}`
                              );
                              notification.success({ message: "Sukses", description: `Goods Receive No. ${goodsReceiveNo} berhasil dibatalkan.` });
                              form.resetFields();
                              setTableDataSource([]);
                              setSelectedRowKeys([]);
                              setAutosaveStatus("stopped");
                              setIsEditMode(false);
                              navigate("/dashboard/data_goods_receive");
                         } catch (error) {
                              console.error("Error voiding Goods Receive:", error);
                              notification.error({ message: "Error", description: "Gagal membatalkan Goods Receive. Silakan coba lagi." });
                              setAutosaveStatus("error");
                         }
                    },
               });
          };

          const handlePrint = async () => {
               const goodsReceiveNo = form.getFieldValue("goods_receive_no");

               if (!goodsReceiveNo) {
                    notification.warning({ message: "Peringatan", description: "Goods Receive No. tidak ditemukan untuk dicetak." });
                    return;
               }

               Modal.confirm({
                    title: 'Konfirmasi',
                    content: 'Cetak dokumen?',
                    okText: 'Ya',
                    cancelText: 'Tidak',
                    onOk: async () => {
                         setPrintLoading(true);
                         setPrintProgress(0);
                         try {
                              setPrintProgress(20);
                              const grPrintDataResponse = await axios.get(
                                   `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-print-data/${goodsReceiveNo}`
                              );
                              const { header, items, ppnValue } = grPrintDataResponse.data;
                              const uniqueDoNos = Array.from(new Set(items.map((item: any) => item.do_no).filter(Boolean)));
                              const doNosString = uniqueDoNos.length > 0 ? uniqueDoNos.join(', ') : '-';
                              const reportUrl = `${process.env.PUBLIC_URL}/reports/GR/index.html`;
                              setPrintProgress(40);
                              const htmlTemplateResponse = await axios.get(reportUrl, { responseType: 'text' });
                              setPrintProgress(70);
                              let htmlContent = htmlTemplateResponse.data;
                              const baseUrl = `${process.env.PUBLIC_URL}/reports/GR/`;
                              htmlContent = htmlContent.replace('<head>', `<head><base href="${baseUrl}">`);
                              htmlContent = htmlContent.replace('fields_supplier', header.supplier_name || '-');
                              htmlContent = htmlContent.replace('fields_address', header.supplier_address || '-');
                              htmlContent = htmlContent.replace('fields_invoice_no', header.supplier_invoice_no || '-');
                              htmlContent = htmlContent.replace('fields_tax_invoice_no', header.tax_invoice_no || '-');
                              htmlContent = htmlContent.replace('fields_doc_no', header.goods_receive_no || '-');
                              htmlContent = htmlContent.replace('fields_doc_date', header.date ? moment(header.date).format('DD/MM/YYYY') : '-');
                              htmlContent = htmlContent.replace('fields_ref_no', header.ref_document_no || '-');
                              htmlContent = htmlContent.replace('fields_warehouse_code', header.warehouse || '-');
                              htmlContent = htmlContent.replace('fields_supplier_do', doNosString);
                              const tableBodyRegex = /<tbody>(.*?)<\/tbody>/s;
                              const tableBodyMatch = htmlContent.match(tableBodyRegex);

                              if (tableBodyMatch && tableBodyMatch[1]) {
                                   const originalTableBodyContent = tableBodyMatch[1];
                                   const itemRowRegex = /<tr>(.*?)<\/tr>/s;
                                   const itemRowMatch = originalTableBodyContent.match(itemRowRegex);

                                   if (itemRowMatch && itemRowMatch[1]) {
                                        const itemRowTemplate = itemRowMatch[0];
                                        let allItemRowsHtml = '';
                                        let totalBeforeVat = 0;

                                        items.forEach((item: any, index: number) => {
                                             let currentRowHtml = itemRowTemplate;
                                             const itemPrice = parseFloat(item.item_price || '0');
                                             const receiveQty = parseFloat(item.receive || '0');
                                             const subTotal = itemPrice * receiveQty;

                                             currentRowHtml = currentRowHtml.replace('>1<', `>${index + 1}<`);
                                             currentRowHtml = currentRowHtml.replace('fields_item_code', item.item_code || '-');
                                             currentRowHtml = currentRowHtml.replace('fields_item_name', item.item_name || '-');
                                             currentRowHtml = currentRowHtml.replace('fields_case', item.case || '0');
                                             currentRowHtml = currentRowHtml.replace('fields_uom', item.uom || '-');
                                             currentRowHtml = currentRowHtml.replace('fields_location', item.location || '-');
                                             currentRowHtml = currentRowHtml.replace('fields_po', item.ref || '0');
                                             currentRowHtml = currentRowHtml.replace('fields_do', item.do || '0');
                                             currentRowHtml = currentRowHtml.replace('fields_receive', item.receive || '0');
                                             currentRowHtml = currentRowHtml.replace('fields_short', item.short || '0');
                                             currentRowHtml = currentRowHtml.replace('fields_dmg', item.dmg || '0');
                                             currentRowHtml = currentRowHtml.replace('fields_over', item.over || '0');
                                             currentRowHtml = currentRowHtml.replace('fields_wrong', item.wrong || '0');
                                             currentRowHtml = currentRowHtml.replace('fields_item_price', itemPrice.toLocaleString('id-ID'));
                                             currentRowHtml = currentRowHtml.replace('fields_sub_total', subTotal.toLocaleString('id-ID'));

                                             allItemRowsHtml += currentRowHtml;
                                             totalBeforeVat += subTotal;
                                        });

                                        htmlContent = htmlContent.replace(originalTableBodyContent, allItemRowsHtml);
                                        const vatAmount = (totalBeforeVat * ppnValue) / 100;
                                        const totalAmount = totalBeforeVat + vatAmount;
                                        htmlContent = htmlContent.replace('fields_before_vat', totalBeforeVat.toLocaleString('id-ID'));
                                        htmlContent = htmlContent.replace('fields_vat', vatAmount.toLocaleString('id-ID'));
                                        htmlContent = htmlContent.replace('fields_total', totalAmount.toLocaleString('id-ID'));
                                        htmlContent = htmlContent.replace('fields_in_words', numberToWordsID(Math.round(totalAmount)).toUpperCase() + ' RUPIAH');
                                        const currentDate = moment().format('DD MM YYYY');
                                        htmlContent = htmlContent.replace('Sampit, dd mm yyyy', `Sampit, ${currentDate}`);

                                   } else {
                                        console.warn("Could not find item row template in tbody.");
                                   }
                              } else {
                                   console.warn("Could not find tbody in HTML template.");
                              }

                              setPrintProgress(100);
                              await new Promise((resolve) => setTimeout(resolve, 500));
                              const printWindow = window.open('', '_blank');
                              if (printWindow) {
                                   printWindow.document.write(htmlContent);
                                   printWindow.document.close();
                                   printWindow.focus();
                              } else {
                                   notification.error({ message: "Error", description: "Gagal membuka jendela cetak. Pastikan pop-up diizinkan." });
                              }

                         } catch (error) {
                              console.error("Error during print operation:", error);
                              notification.error({ message: "Error", description: "Gagal mencetak Goods Receive. Silakan coba lagi." });
                         } finally {
                              setPrintLoading(false);
                              setPrintProgress(0);
                         }
                    }
               });
          };
          return (
               <>
                    <WrappedComponent
                         {...(props as P)}
                         onDateChange={onDateChange}
                         onChange={onChange}
                         onItemGroupChange={onItemGroupChange}
                         columns={columns}
                         rowSelection={rowSelection}
                         isModalOpen={isModalOpen}
                         showModal={showModal}
                         handleCancel={handleCancel}
                         poData={poData}
                         form={form}
                         onSelectPo={handlePoSelection}
                         tableDataSource={tableDataSource}
                         isEditModalOpen={isEditModalOpen}
                         showEditModal={showEditModal}
                         handleEditCancel={handleEditCancel}
                         selectedPoItemForEdit={selectedPoItemForEdit}
                         onRowDoubleClick={handleRowDoubleClick}
                         onUpdatePoItem={handleUpdatePoItem}
                         onSelectLocationCode={handleSelectLocationCodeForEdit}
                         isAddLineModalOpen={isAddLineModalOpen}
                         showAddLineModal={showAddLineModal}
                         handleAddLineCancel={handleAddLineCancel}
                         refDocumentNo={form.getFieldValue("ref_document_no")}
                         onAddLineItems={handleAddLineItems}
                         onDeleteSelectedItems={handleDeleteSelectedItems}
                         initialSelectedAddLineKeys={initialSelectedAddLineKeys}
                         autosaveStatus={autosaveStatus}
                         existingGrPoNos={existingGrPoNos}
                         tableSummary={tableSummary}
                         onVoid={handleVoid}
                         onSubmit={handleSubmit}
                         onPrint={handlePrint}
                         printLoading={printLoading}
                         printProgress={printProgress}
                         warehouseData={warehouseData}
                         submitLoading={submitLoading}
                         lockedFields={lockedFields}
                         onFieldFocus={onFieldFocus}
                         onFieldBlur={onFieldBlur}
                         onFormValuesChange={onFormValuesChange}
                         getLockProps={getLockProps}
                    />
                    <Modal
                         open={submitLoading}
                         title="Progress..."
                         footer={null}
                         closable={false}
                         centered
                    >
                         <div style={{ textAlign: 'center', padding: '20px' }}>
                              <Spin size="large" />
                              <div style={{ marginTop: '15px', fontWeight: 'bold' }}>Please Wait...</div>
                         </div>
                    </Modal>
                    <Modal
                         open={printLoading}
                         title="Progress..."
                         footer={null}
                         closable={false}
                         centered
                    >
                         <div style={{ textAlign: 'center', padding: '20px' }}>
                              <Progress type="circle" percent={printProgress} />
                              <div style={{ marginTop: '15px', fontWeight: 'bold' }}>Please Wait...</div>
                         </div>
                    </Modal>
               </>
          );
     };
     return HOC;
};

export { withGoodsReceiveLogic };